import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { FaCheckCircle } from "react-icons/fa";
import { DataTable } from "components/molecules/table/table";
import { DataTableSkeleton, PageHeader, StatCard, StatusPill } from "components/admin";
import {
  PrimaryActionButton,
  SectionStatsGrid,
} from "pages/app/workspace/sections/styles/workspace-section.styles";
import { workspaceApi } from "pages/app/workspace/services";
import { formatDateTime, getApiErrorMessage } from "utils/admin";
import {
  NotificationsSectionSection,
  NotificationsSectionLabelText,
  NotificationsSectionMetaText,
} from "pages/app/workspace/sections/styles/notifications-section.inline.styles";
export const NotificationsSection: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: response, isLoading } = useQuery({
    queryKey: ["workspace-notifications"],
    queryFn: () =>
      workspaceApi.notifications.list({
        page: 1,
        per_page: 50,
      }),
  });
  const notifications = response?.notifications || [];
  const unreadCount =
    response?.meta?.unread_count ?? notifications.filter((item: any) => !item.read_at).length;
  const readMutation = useMutation({
    mutationFn: (id: string | number) => workspaceApi.notifications.read(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["workspace-notifications"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to mark this notification as read."));
    },
  });
  const readAllMutation = useMutation({
    mutationFn: () => workspaceApi.notifications.readAll(),
    onSuccess: () => {
      toast.success("All notifications marked as read.");
      queryClient.invalidateQueries({
        queryKey: ["workspace-notifications"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to mark notifications as read."));
    },
  });
  const removeMutation = useMutation({
    mutationFn: (id: string | number) => workspaceApi.notifications.remove(id),
    onSuccess: () => {
      toast.success("Notification dismissed.");
      queryClient.invalidateQueries({
        queryKey: ["workspace-notifications"],
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to dismiss this notification."));
    },
  });
  return (
    <>
      <PageHeader
        title="Notifications"
        description="Review inbox activity, mark items as read, and clear messages that no longer need attention."
        actions={
          <PrimaryActionButton
            disabled={readAllMutation.isPending || unreadCount === 0}
            onClick={() => readAllMutation.mutate()}
            type="button"
          >
            <FaCheckCircle size={12} />
            Mark all as read
          </PrimaryActionButton>
        }
      />

      <SectionStatsGrid>
        <StatCard label="Messages" value={notifications.length} />
        <StatCard label="Unread" value={unreadCount} />
        <StatCard label="Read" value={Math.max(notifications.length - unreadCount, 0)} />
        <StatCard label="Page Size" value={response?.meta?.per_page ?? 50} />
      </SectionStatsGrid>

      <NotificationsSectionSection>
        <DataTable
          canEdit={false}
          columns={[
            {
              label: "Notification",
              accessor: (row: any) => (
                <div>
                  <NotificationsSectionLabelText>{row.title}</NotificationsSectionLabelText>
                  <NotificationsSectionMetaText>
                    {row.body || "No body provided."}
                  </NotificationsSectionMetaText>
                </div>
              ),
            },
            {
              label: "Category",
              accessor: (row: any) => <StatusPill value={row.category} />,
            },
            {
              label: "Screen",
              accessor: (row: any) => row.screen || "Unknown",
            },
            {
              label: "Status",
              accessor: (row: any) => <StatusPill value={row.read_at ? "read" : "pending"} />,
            },
            {
              label: "Created",
              accessor: (row: any) => formatDateTime(row.created_at),
            },
          ]}
          data={notifications}
          emptyDescription="Notifications will appear here when the API records inbox activity."
          emptyTitle="No notifications"
          isLoading={isLoading}
          loadingState={<DataTableSkeleton columns={5} rows={5} />}
          onRemove={(id, row) => {
            if (row?.id) {
              removeMutation.mutate(row.id);
            } else if (id) {
              removeMutation.mutate(String(id));
            }
          }}
          onView={(id, row) => {
            if (row?.read_at) {
              return;
            }
            readMutation.mutate(String(row?.id || id));
          }}
        />
      </NotificationsSectionSection>
    </>
  );
};
